import React, { useEffect, useMemo } from 'react';
import { Modal, Form, message, Row, Col } from 'antd';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import ModelField from '@/components/Form/ModelField';

export interface ModelModalProps<T = any> {
  /** Modal visibility */
  visible: boolean;
  /** Modal title */
  title?: string;
  /** Modal width */
  width?: number | string;
  /** Field definitions for the form */
  fields: API.FieldType[];
  /** Initial values for form fields */
  initialValues?: Partial<T>;
  /** Data for the item being edited (if editing) */
  editingItem?: T;
  /** Loading state */
  loading?: boolean;
  /** Validation rules */
  validationRules?: Record<string, any>;
  /** Zod validation schema */
  validationSchema?: z.ZodSchema<any>;
  /** Cancel handler */
  onCancel: () => void;
  /** Success handler */
  onSuccess: () => void;
  /** Submit handler - receives the form data */
  onSubmit: (data: T, isEditing: boolean) => Promise<void>;
  /** Custom form data transformer - called before submit */
  transformData?: (data: any, isEditing: boolean) => T;
  /** Custom initial data transformer - called when setting form values */
  transformInitialData?: (data: any) => any;
}

function ModelModal<T = any>({
  visible,
  title,
  width,
  fields = [],
  initialValues = {},
  editingItem,
  loading = false,
  validationRules = {},
  validationSchema,
  onCancel,
  onSuccess,
  onSubmit,
  transformData,
  transformInitialData,
}: ModelModalProps<T>) {
  // Calculate modal width based on field count (keep for sizing but not for layout)
  const formLayout = useMemo(() => {
    const visibleFields = fields.filter(field => !field.hideInForm);
    const fieldCount = visibleFields.length;
    
    // If more than 8 fields, increase modal width (but layout is controlled by grid)
    const modalWidth = width || (fieldCount > 8 ? 900 : 600);
    
    return {
      modalWidth,
      fieldCount,
      visibleFields,
    };
  }, [fields, width]);

  // Prepare default values from field definitions
  const prepareDefaultValues = () => {
    const defaults: any = {};
    fields.forEach(field => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return { ...defaults, ...initialValues };
  };

  const methods = useForm<API.defaultValueType>({
    defaultValues: prepareDefaultValues(),
    mode: 'onChange',
    resolver: validationSchema ? zodResolver(validationSchema as any) : undefined,
  });

  const { handleSubmit, reset, setValue, control, formState: { errors } } = methods;

  const isEditing = !!editingItem;

  // Set form values when modal opens or editing item changes
  useEffect(() => {
    if (visible) {
      if (isEditing && editingItem) {
        // Transform editing data if transformer provided
        const dataToSet = transformInitialData 
          ? transformInitialData(editingItem)
          : editingItem;
        
        // Set form values field by field based on field definitions
        fields.forEach(field => {
          const value = (dataToSet as any)[field.name];
          if (value !== undefined) {
            
            // Special handling for dateRange to ensure it's not triggering dayjs errors
            if (field.name === 'dateRange' && Array.isArray(value)) {
              // Only set if all values in array are valid dayjs objects
              const validDayjsArray = value.every(v => v && typeof v.isValid === 'function' && v.isValid());
              if (validDayjsArray) {
                setValue(field.name as any, value);
              } else {
                // console.log('ModelModal - skipping invalid dateRange array');
              }
            } else {
              setValue(field.name as any, value);
            }
          }
        });
      } else {
        // Set initial values for creation
        Object.entries(initialValues).forEach(([key, value]) => {
          
          // Special handling for dateRange to ensure it's not triggering dayjs errors
          if (key === 'dateRange' && Array.isArray(value)) {
            // Only set if all values in array are valid dayjs objects
            const validDayjsArray = value.every(v => v && typeof v.isValid === 'function' && v.isValid());
            if (validDayjsArray) {
              setValue(key as any, value);
            } else {
              console.log('ModelModal - skipping invalid initial dateRange array');
            }
          } else {
            setValue(key as any, value);
          }
        });
      }
    } else {
      // Reset form when modal closes
      reset();
    }
  }, [visible, editingItem, initialValues, fields, setValue, reset, transformInitialData, isEditing]);

  const handleFormSubmit = async (data: API.defaultValueType) => {
    try {
      // Transform data if transformer provided
      const finalData = transformData 
        ? transformData(data, isEditing)
        : data as T;

      await onSubmit(finalData, isEditing);
      
      message.success(
        isEditing 
          ? 'Item updated successfully' 
          : 'Item created successfully'
      );
      
      onSuccess();
    } catch (error) {
      console.error('Error saving item:', error);
      message.error(
        `Failed to ${isEditing ? 'update' : 'create'} item. Please check the form and try again.`
      );
    }
  };

  const modalTitle = title || `${isEditing ? 'Edit' : 'Create'} Item`;

  // Render fields using grid layout from field definitions
  const renderFields = () => {
    return (
      <Row gutter={16}>
        {formLayout.visibleFields.map((field) => {
          const span = field.grid || 24; // Default to full width if no grid specified
          
          return (
            <Col key={field.name} span={span}>
              <ModelField
                field={field}
                control={control}
                setValue={setValue}
                errors={errors}
              />
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      style={{ top: 50 }}
      onOk={handleSubmit(handleFormSubmit)}
      confirmLoading={loading}
      width={formLayout.modalWidth}
      destroyOnClose
    >
      <FormProvider {...methods}>
        <Form layout="vertical" preserve={false}>
          {renderFields()}
        </Form>
      </FormProvider>
    </Modal>
  );
}

export default ModelModal;
